import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertCircle, MailCheck } from 'lucide-react';
import api from '@/lib/api';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const started = useRef(false);
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setStatus('missing');
      return;
    }

    const confirm = async () => {
      try {
        await api.post('/auth/verify-email/confirm', { token });
        if (user) await refreshUser();
        setStatus('success');
      } catch (error) {
        console.warn('Email verification failed.');
        setStatus('error');
      }
    };
    confirm();
  }, [refreshUser, user]);

  const success = status === 'success';
  const checking = status === 'checking';
  const title = checking
    ? t('verifyEmail.title')
    : success
      ? t('verifyEmail.successTitle')
      : t('verifyEmail.invalidTitle');
  const description = checking
    ? t('verifyEmail.checking')
    : success
      ? t('verifyEmail.successDesc')
      : status === 'missing'
        ? t('verifyEmail.missingToken')
        : t('verifyEmail.invalidDesc');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4"><LanguageSelector /></div>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              {checking ? (
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
              ) : success ? (
                <CheckCircle className="w-12 h-12 text-emerald-600" />
              ) : (
                <AlertCircle className="w-12 h-12 text-red-600" />
              )}
            </div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          {!checking && (
            <CardContent>
              <Button
                className="w-full"
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                data-testid="button-verification-continue"
              >
                <MailCheck className="w-4 h-4 mr-2" />
                {user ? t('verifyEmail.goToDashboard') : t('verifyEmail.goToLogin')}
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
